/**
 * KÖRVONAL — the cart's "Szállítás" card: delivery-method picker and the
 * free-shipping bar in one box, right above the total.
 *
 * PickupBird (Digiloop) mounts its Házhozszállítás / Csomagpont selector as an
 * app block in its own template section, which Shopify stacks BELOW the cart
 * grid. On desktop that left the choice stranded under ~600px of empty space
 * while the Pénztár button sat far above it, so the picker read as an appendix
 * to the page rather than a step in the checkout. Lifting it to the end of the
 * left column (the first fix) still split shipping in two: the method on the
 * left, what it costs ("Már csak … az ingyenes szállításig") on the right.
 *
 * So the picker now goes into a card of its own in the right column, directly
 * above #AjaxCartSubtotal, whose first child is the free-shipping bar's
 * outlined widget. CSS joins the two outlines into one box: title, the two
 * tiles, then the bar. The picker's own heading ("Válassz szállítási módot:")
 * and subtitle (an untranslated "Here you can choose between delivery methods"
 * on the HU cart) give way to the card title, and its 140px tiles are
 * compacted to fit the narrow column.
 *
 * WHY THE CARD SITS NEXT TO #AjaxCartSubtotal AND NOT INSIDE IT
 *   component-cart.js replaces #AjaxCartSubtotal's innerHTML after every
 *   quantity change. A picker inside it would be detached on the first click
 *   of a + button. The bar is re-rendered each time, the card never is; the
 *   join is pure CSS (:has), so it holds across re-renders, and where the bar
 *   is absent (no tier for the country, setting off) the card keeps its own
 *   full outline.
 *
 * WHY A NODE MOVE AND NOT innerHTML
 *   The picker is a mounted app with its own listeners and internal state. A
 *   move with appendChild relocates the same element, so handlers and state
 *   survive; assigning innerHTML anywhere in its ancestry would destroy and
 *   recreate the nodes and silently unbind everything — which is exactly how
 *   the cart's adószám field lost its listeners once before.
 *
 * WHY IT MATTERS THAT THIS IS THE PICKUP WIDGET
 *   The locker + COD flow is a two-app chain keyed on the shipping rate name.
 *   If this element stops working, pickup-point orders stop being placeable, so
 *   the move is deliberately conservative: it relocates the block, dispatches a
 *   resize so anything that measured its old container re-measures, and does
 *   nothing at all if the layout is not the one it expects. The locker map is
 *   a full-screen overlay, so the narrow column does not squeeze it. All
 *   restyling is CSS on PickupBird's stable pp-provider-* classes, never its
 *   hashed module classes.
 *
 * A gift-card-only cart (korvonal-digital-cart.liquid) hides the picker and the
 * bar, and an emptied cart hides the subtotal; the card goes with them.
 *
 * TO REVERT: delete the script tag from sections/main-cart.liquid. The block
 * goes back to where Shopify puts it, and nothing else changes.
 */
(function () {
  'use strict';

  // The card title per storefront language. A language missing here gets no
  // title, and PickupBird's own heading stays visible instead.
  var TITLES = { hu: 'Szállítás', en: 'Shipping', de: 'Versand', fr: 'Livraison', pl: 'Dostawa', cs: 'Doprava' };

  var CSS = [
    'html[data-kv-digital-cart] #kv-ship-card { display: none !important; }',
    // An emptied cart hides #AjaxCartSubtotal through this class; the card goes too.
    '.cart-section--empty #kv-ship-card { display: none !important; }',
    '#kv-ship-card .kv-ship-card__title { margin: 0 0 10px; font-weight: 400; }',
    '#kv-ship-card.kv-ship-card--titled .pp-provider-title-container { display: none !important; }',
    '#kv-ship-card .pp-provider-selector-container,',
    '#kv-ship-card .pp-provider-selector-select-delivery-type-button { padding: 0 !important; }',
    '#kv-ship-card .pp-provider-select-delivery-type-buttons-container { gap: 10px !important; }',
    '#kv-ship-card .pp-provider-select-delivery-type-buttons-container > * { height: auto !important; padding: 12px 6px !important; }',
    '#kv-ship-card .pp-provider-select-delivery-type-buttons-container > * > div { --st-margin-top: 0px !important; --st-margin-bottom: 0px !important; }',
    // The app sets its own font; the tile labels take the theme's, like the
    // card title and the bar around them.
    '#kv-ship-card .pp-provider-select-delivery-type-buttons-container * { font-family: var(--kv-ship-font) !important; }',
    // The join: the card loses its bottom edge, the bar widget its top edge.
    '#kv-ship-card:has(+ #AjaxCartSubtotal > .sidebar-widget:first-child shipping-notice) { margin-bottom: 0; }',
    '#kv-ship-card:has(+ #AjaxCartSubtotal > .sidebar-widget:first-child shipping-notice) > .sidebar-widget__content { border-bottom: 0; border-bottom-left-radius: 0; border-bottom-right-radius: 0; padding-bottom: 0; }',
    '#kv-ship-card + #AjaxCartSubtotal > .sidebar-widget:first-child:has(shipping-notice) > .sidebar-widget__content { border-top: 0; border-top-left-radius: 0; border-top-right-radius: 0; }'
  ].join('\n');

  function buildCard() {
    var style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    var card = document.createElement('div');
    card.id = 'kv-ship-card';
    card.className = 'sidebar-widget sidebar-widget--outline gutter-regular margin-bottom--regular';
    var content = document.createElement('div');
    content.className = 'sidebar-widget__content';
    card.appendChild(content);

    var title = TITLES[(document.documentElement.lang || '').slice(0, 2).toLowerCase()];
    if (title) {
      var p = document.createElement('p');
      p.className = 'kv-ship-card__title';
      p.textContent = title;
      content.appendChild(p);
      card.classList.add('kv-ship-card--titled');
    }
    return card;
  }

  function lift() {
    // Shopify puts the app's name in the block's ID and its inner wrapper's
    // class, but never both on one element — matching only `.shopify-app-block`
    // with a class filter finds nothing at all.
    var hit = document.querySelector('[id*="pickupbird"], [class*="pickupbird"]');
    var block = hit && (hit.closest('.shopify-app-block') || hit);
    if (!block || block.dataset.kvLifted) return !!block;
    // Where PickupBird does not run (andwetalk.com, 2026-10-26) its block stays an
    // empty shell; a card around it would be a bare "Shipping" title. Wait for the
    // app's own markup, and give up quietly if it never comes.
    if (!block.querySelector('[class*="pp-provider"]')) return false;

    var section = block.closest('.shopify-section');
    var cart = document.querySelector('.cart-section');
    var subtotal = cart && cart.querySelector('#AjaxCartSubtotal');
    // The cart grid (two columns on desktop, one on phones) is the layout this
    // expects; anything else keeps the picker where Shopify put it.
    if (!section || !subtotal || getComputedStyle(cart).display !== 'grid') return true;

    block.dataset.kvLifted = '1';
    var card = buildCard();
    card.firstChild.appendChild(block);
    subtotal.parentNode.insertBefore(card, subtotal);
    card.style.setProperty('--kv-ship-font', getComputedStyle(card).fontFamily);

    // The empty shell the block came from would otherwise leave a gap.
    if (section.textContent.trim() === '') section.style.display = 'none';

    // Anything that sized itself against the old container gets a chance to
    // re-measure — the map in particular.
    window.dispatchEvent(new Event('resize'));
    return true;
  }

  // The app mounts asynchronously, so poll briefly rather than racing it. Give
  // up after ~10s: a picker left where Shopify put it is a cosmetic problem, a
  // script retrying forever is a real one.
  var tries = 0;
  var timer = setInterval(function () {
    if (lift() || ++tries > 40) {
      clearInterval(timer);
      hideEmptyShell();
    }
  }, 250);

  // The picker never drew: its section is an empty shell that would leave a gap.
  function hideEmptyShell() {
    var hit = document.querySelector('[id*="pickupbird"], [class*="pickupbird"]');
    var block = hit && (hit.closest('.shopify-app-block') || hit);
    var section = block && !block.dataset.kvLifted && block.closest('.shopify-section');
    if (section && section.textContent.trim() === '') section.style.display = 'none';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lift);
  } else {
    lift();
  }
})();
